import { de, es, nl, nlBE } from 'date-fns/locale'
import { objectKeys } from './object'

/**
 * @deprecated Use CountryCode from @trustoo/types/locale instead
 */
export type CountryCode = 'NL' | 'BE' | 'DE' | 'ES'
export type CountryCodeUpperLower = CountryCode | Lowercase<CountryCode>

// force all countries to be present
type PerCountryMap<ValueType = string> = {
    [Property in CountryCode]: ValueType
}

/** @deprecated Use formatNumber from @trustoo/funcs/number instead */
export function formatNumber(number: bigint | number, nrFractionDigits: number = 2) {
    return new Intl.NumberFormat(getLocale(), {
        minimumFractionDigits: nrFractionDigits,
        maximumFractionDigits: nrFractionDigits,
    }).format(number)
}

export const formatPhoneDigitsOnly = (phone: string) => (phone || '').replace(/\D+/g, '')

/**
 * format with locale-specific formatting:
 * - Dutch (nl): 12,00 => 12,- (dash for whole numbers, comma as decimal separator)
 * - Belgian Dutch (nl-BE): 12,00 => 12,- (dash for whole numbers, comma as decimal separator)
 * - German (de-DE): 12.00 => 12 (rounded whole numbers, dot as decimal separator)
 * - Spanish (es-ES): 12.00 => 12 (rounded whole numbers, dot as decimal separator)
 * - Other locales: standard number formatting
 * no currency (psychological better for conversion)
 */
export function formatPriceWithoutCurrency(number: bigint | number) {
    const locale = getLocale()

    if (locale === 'nl' || locale === 'nl-BE') {
        // Dutch/Belgian Dutch: replace .00 with dash, use comma as decimal separator
        return formatNumber(number).replace(/0{2}$/, '-')
    } else if (locale === 'de-DE' || locale === 'es-ES') {
        // German/Spanish: use dot as decimal separator, remove .00 for whole numbers
        const roundedValue = Math.round(Number(number) * 100) / 100
        if (roundedValue % 1 === 0) {
            return roundedValue.toString()
        }
        return roundedValue.toFixed(2)
    } else {
        // Other locales: fallback to Dutch style
        return formatNumber(number).replace(/0{2}$/, '-')
    }
}

/**
 * format with locale-specific currency display:
 * - Dutch (nl): € symbol before, 12,00 => €12,- (comma as decimal separator)
 * - Belgian Dutch (nl-BE): € symbol before, 12,00 => €12,- (comma as decimal separator)
 * - German (de-DE): € symbol after, 12.00 => 12 € (dot as decimal separator)
 * - Spanish (es-ES): € symbol after, 12.00 => 12 € (dot as decimal separator)
 * - Other locales: € symbol before (fallback to Dutch style)
 */
export function formatPriceWithCurrency(number: number) {
    const locale = getLocale()
    const formattedNumber = formatPriceWithoutCurrency(Math.abs(number))

    if (locale === 'nl' || locale === 'nl-BE') {
        // Dutch/Belgian Dutch: € symbol before, dash for whole numbers
        return `${number < 0 ? '-' : ''}€${formattedNumber}`
    } else if (locale === 'de-DE' || locale === 'es-ES') {
        // German/Spanish: € symbol after with space
        return `${number < 0 ? '-' : ''}${formattedNumber} €`
    } else {
        // Other locales: fallback to Dutch style with € symbol
        return `${number < 0 ? '-' : ''}€${formattedNumber}`
    }
}

/**
 * Automatically format price based on locale preferences:
 * - ES and DE: default to formatPriceWithCurrency (with currency)
 * - NL and BE: default to formatPriceWithoutCurrency (without currency)
 * - Other locales: fallback to formatPriceWithoutCurrency
 */
export function formatPrice(number: bigint | number) {
    const locale = getLocale()

    if (locale === 'de-DE' || locale === 'es-ES') {
        // German and Spanish: show currency by default
        return formatPriceWithCurrency(Number(number))
    } else {
        // Dutch, Belgian Dutch, and other locales: no currency by default
        return formatPriceWithoutCurrency(number)
    }
}

export function getLocale() {
    // locale format: <languageCode>-<countryCode>
    const { hostname } = window.location
    if (hostname.includes('trustlocal.be')) {
        return 'nl-BE'
    }
    if (hostname.includes('trustlocal.de')) {
        return 'de-DE'
    }
    if (hostname.includes('trustlocal.es')) {
        return 'es-ES'
    }
    return 'nl'
}

export function getCountryCode() {
    const locale = getLocale()
    if (locale.length > 2) {
        return locale.split('-')[1] as 'BE' | 'DE' | 'ES'
    }
    return locale.toUpperCase() as 'NL'
}

export function getCompanyBaseUrl() {
    if (typeof window === 'undefined') return ''

    const { protocol, hostname } = window.location

    if (hostname.startsWith('localhost') || hostname.startsWith('l.')) {
        return `${protocol}//${hostname}:${process.env.REACT_APP_LOCAL_NEXT_PORT || '3000'}`
    }

    return `https://${hostname}`
}

export function getLoginUrl(redirect?: string) {
    if (typeof window === 'undefined') {
        return '/login'
    }

    const target = redirect ?? window.location.href
    return `${getCompanyBaseUrl()}/login?redirect=${encodeURIComponent(target)}`
}

export const publicBucketUrl: PerCountryMap = {
    NL: 'https://static.trustoo.nl',
    BE: 'https://static.trustlocal.be',
    DE: 'https://static.trustlocal.de',
    ES: 'https://static.trustlocal.es',
}

export const getPublicBucketUrl = () => publicBucketUrl[getCountryCode()]

export function getDateFnsLocale() {
    return {
        BE: nlBE,
        DE: de,
        NL: nl,
        ES: es,
    }[getCountryCode()]
}

// https://en.wikipedia.org/wiki/Regional_Indicator_Symbol
// https://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
export const getFlagEmoji = (countryCode: string): string => {
    const transCharCode = (charCode: number) => {
        const codePoint = 0x1f1e6 + charCode - 'A'.charCodeAt(0)
        return [
            Math.floor((codePoint - 0x10000) / 0x400) + 0xd800,
            ((codePoint - 0x10000) % 0x400) + 0xdc00,
        ]
    }

    countryCode = countryCode.toUpperCase()
    return String.fromCharCode(
        ...transCharCode(countryCode.charCodeAt(0)),
        ...transCharCode(countryCode.charCodeAt(1)),
    )
}

export const validateVat = (val: string, paymentInfo: { country_code?: CountryCode }) =>
    !val ||
    Boolean(
        val.match(
            countrySettings[
                (paymentInfo.country_code?.toUpperCase() || getCountryCode()) as CountryCode
            ].vatRegex,
        ),
    )

/**
 * Supports Spanish CIF, NIF and NIE formats.
 */
export const validateCifNifNie = (val: string) =>
    !val || /^(?:[A-Z]\d{8}|\d{8}[A-Z]|[XYZ]\d{7}[A-Z])$/i.test(val)

export const countrySettings = {
    NL: {
        streetNumberExtra: false,
        wideStreetNumberExtra: false,
        useVatNumber: false,
        vatNumberInput: false,
        useCifNifNie: false,
        useQooqie: true,
        vatRegex: /^NL[0-9]{9}B[0-9]{2}$/,
        urlActivationAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/activatiegesprek-trustoo',
        urlPresenceOptInAppointment: null as string | null,
        urlPresenceOptInAppointmentSandbox: null as string | null,
        urlIntroductionAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/adviesgesprek-trustoo',
        calendlyOrganizationSlug: 'trustoo-succes',
        calendlyOnboardingCallEventSlug: 'onboarding-trustoo',
    },
    BE: {
        streetNumberExtra: false,
        wideStreetNumberExtra: false,
        useVatNumber: true,
        vatNumberInput: false,
        useCifNifNie: false,
        useQooqie: true,
        vatRegex: /^BE[0-9]{10}$/,
        urlActivationAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/activatiegesprek-trustlocal',
        urlPresenceOptInAppointment: null as string | null,
        urlPresenceOptInAppointmentSandbox: null as string | null,
        urlIntroductionAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/kennismaking-trustlocal-validation-page',
        calendlyOrganizationSlug: 'trustlocal-belgie',
        calendlyOnboardingCallEventSlug: 'onboarding-trustlocal',
    },
    DE: {
        streetNumberExtra: false,
        wideStreetNumberExtra: false,
        useVatNumber: false,
        vatNumberInput: false,
        useCifNifNie: false,
        useQooqie: false,
        vatRegex: /^DE[0-9]{5,12}$/,
        urlActivationAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/aktivierungsgesprach-trustlocal',
        urlPresenceOptInAppointment:
            'https://meetings-eu1.hubspot.com/trustlocaltrustoo/subscription-meeting' as
                | string
                | null,
        urlPresenceOptInAppointmentSandbox:
            'https://meetings-eu1.hubspot.com/ffesch/test-subscription-lawyers-de' as string | null,
        urlIntroductionAppointment:
            'https://trustoo.trustlocal.com/meetings/rr/einfuhrungsgesprach-trustlocal-validation-page',
        calendlyOrganizationSlug: 'trustlocal-deutschland',
        calendlyOnboardingCallEventSlug: 'onboarding-trustlocal',
    },
    ES: {
        streetNumberExtra: true,
        wideStreetNumberExtra: true,
        useVatNumber: true,
        vatNumberInput: true,
        useCifNifNie: true,
        useQooqie: false,
        vatRegex: /^ES[0-9A-Z]{9}$/,
        urlActivationAppointment:
            'https://trustoo.trustlocal.com/meetings/jeremy-stevens/sales-es-round-robin',
        urlPresenceOptInAppointment: null as string | null,
        urlPresenceOptInAppointmentSandbox: null as string | null,
        urlIntroductionAppointment:
            'https://trustoo.trustlocal.com/meetings/jeremy-stevens/sales-es-round-robin',
        calendlyOrganizationSlug: 'trustlocal-espana',
        calendlyOnboardingCallEventSlug: 'entrevista-de-introduccion-trustlocal',
    },
}

export const countryCodes = objectKeys(countrySettings)

export const getCalendlyOnboardingUrl = (countryCode?: CountryCode) => {
    const settings = countrySettings[countryCode ?? (getCountryCode() as CountryCode)]
    return `https://calendly.com/${settings.calendlyOrganizationSlug}/${settings.calendlyOnboardingCallEventSlug}`
}

// TRUS-8068: HubSpot Meetings URL for the Presence opt-in CS contact flow.
// Distinct from `urlActivationAppointment` (used by OnboardingStepThankYou)
// because CS routes subscription conversations to a dedicated page. Returns
// null when no URL is configured (e.g. NL/BE/ES) so callers fall back to
// the inline ContactUs panel instead of a broken iframe.
export const getPresenceOptInAppointmentUrl = (opts: {
    isTest: boolean
    countryCode?: CountryCode
}): string | null => {
    const settings = countrySettings[opts.countryCode ?? (getCountryCode() as CountryCode)]
    if (opts.isTest) {
        return settings.urlPresenceOptInAppointmentSandbox
    }
    return settings.urlPresenceOptInAppointment
}

export function getCalendlyOnboardingCallSlugList() {
    return [
        ...new Set(
            Object.values(countrySettings).map(
                (setting) => setting.calendlyOnboardingCallEventSlug,
            ),
        ),
    ]
}
